"use client"

import React, { HTMLAttributes, useCallback, useMemo, useState } from "react"
import Link, { type LinkProps } from "next/link"
import { APP_B2B_ACCOUNT_OWNER, APP_B2B_OCI_USER } from "@/constants"
import {
  BookMarked,
  Boxes,
  Building2,
  ChevronDownIcon,
  ChevronUpIcon,
  FileCog,
  FilePenLine,
  CheckCircle2,
  XCircle,
  Home,
  ImageIcon,
  MapPinHouse,
  Package,
  ShoppingCart,
  UserCog,
  type LucideIcon,
} from "lucide-react"

import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { hasAnyRole } from "@/lib/role-helper"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { cn } from "@/lib/utils"
import { isSbLocaleDach } from "@/components/ui/helpers/utils"
import StoryblokLogoutButton from "@/components/storyblok/header/StoryblokLogoutButton"

interface AccountNavigationLinkProps extends LinkProps, HTMLAttributes<HTMLAnchorElement> {
  text?: string
  icon?: LucideIcon
  active?: boolean
  sbLocale?: string
  b2bOnly: boolean
  ociOnly?: boolean
  hidden: boolean
}

function AccountNavigationLink(props: AccountNavigationLinkProps) {
  return (
    <Link
      prefetch={false}
      href={getLinkWithPrefixFromURLAndLocale(props.href as string, props.sbLocale) as string}
      className={cn(
        `flex w-full items-center bg-card py-2.5 pr-2.5 pl-3 leading-7 hover:text-primary`,
        {
          "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground": props.active,
        },
        props.className
      )}
    >
      {props.icon && <props.icon className={"mr-4 inline-block"} size={20} />} {props.text}
    </Link>
  )
}

interface AccountNavigationProps {
  activeMenuItem: string
  sbLocale: string
  isB2B: boolean
  storyBlokLabels: { [key: string]: string }
  accountContext: AccountContext
  isOci: boolean
}

export default function AccountNavigationClient(props: AccountNavigationProps) {
  const hasB2BAccountOwnerRole = props.isB2B && hasAnyRole(props.accountContext, [APP_B2B_ACCOUNT_OWNER])
  const hasOCIUserRole = props.isB2B && hasAnyRole(props.accountContext, [APP_B2B_OCI_USER])
  const hasApproverRole = props.isB2B && hasAnyRole(props.accountContext, ["APP_B2B_APPROVER"]) 

  const accountNavigationLinks: AccountNavigationLinkProps[] = [
    {
      href: "/account/dashboard",
      // text: "account.nav.dashboard",
      id: "dashboard",
      icon: Home,
      b2bOnly: false,
      hidden: false,
    },
    {
      href: "/account/orders",
      // text: "account.nav.orders",
      id: "orders",
      icon: Package,
      b2bOnly: false,
      hidden: hasOCIUserRole,
    },
    {
      href: "/account/articles",
      // text: "account.nav.articles",
      id: "articles",
      icon: ShoppingCart,
      b2bOnly: false,
      hidden: hasOCIUserRole,
    },
    {
      href: "/account/wishlists",
      // text: "account.nav.wishlists",
      id: "wishlists",
      icon: BookMarked,
      b2bOnly: false,
      hidden: hasOCIUserRole,
    },
    {
      href: "/account/approvals/open",
      id: "approvals_open",
      icon: CheckCircle2,
      b2bOnly: true,
      hidden: hasOCIUserRole || !props.isB2B,
    },
    {
      href: "/account/approvals/rejected",
      id: "approvals_rejected",
      icon: XCircle,
      b2bOnly: true,
      hidden: hasOCIUserRole || !props.isB2B,
    },
    {
      href: "/account/addresses",
      // text: "account.nav.addresses",
      id: "addresses",
      icon: MapPinHouse,
      b2bOnly: false,
      hidden: hasOCIUserRole,
    },
    {
      href: "/account/profile",
      // text: "account.nav.profile",
      id: "profile",
      icon: Building2,
      b2bOnly: false,
      hidden: hasOCIUserRole,
    },
    {
      href: "/account/quotes",
      // text: "account.nav.quotes",
      id: "quotes",
      icon: FilePenLine,
      b2bOnly: true,
      hidden: hasOCIUserRole || !isSbLocaleDach(props.sbLocale) || (props.isOci && hasB2BAccountOwnerRole),
    },
    {
      href: "/account/users",
      // text: "account.nav.usermanagement",
      id: "usermanagement",
      icon: UserCog,
      b2bOnly: true,
      hidden: !hasB2BAccountOwnerRole,
    },
    {
      href: "/account/oci/punchout",
      // text: "account.nav.oci.punchout",
      id: "oci_punchout",
      icon: FileCog,
      b2bOnly: true,
      ociOnly: true,
      hidden: !hasB2BAccountOwnerRole,
    },
    {
      href: "/account/oci/mapping",
      // text: "account.nav.oci.mapping",
      id: "oci_mapping",
      icon: Boxes,
      b2bOnly: true,
      ociOnly: true,
      hidden: !hasB2BAccountOwnerRole,
    },
    {
      href: "/account/oci/branding",
      // text: "account.nav.oci.branding",
      id: "oci_branding",
      icon: ImageIcon,
      b2bOnly: true,
      ociOnly: true,
      hidden: !hasB2BAccountOwnerRole,
    },
    /*,
  {
    href: "/account/subscriptions",
    text: "account.nav.subscriptions",
    id: "subscriptions",
    icon: Mails,
    b2bOnly: false,
  },*/
  ]

  // console.log("roles", props.accountContext.roles?.userRoles?.map((t) => t.roleID))
  // console.log("sbLocale", props.sbLocale)
  // console.log("oci", props.isOci)

  const [open, setOpen] = useState<boolean>(false)
  const toggle = useCallback((): void => setOpen((state) => !state), [])
  return (
    <>
      <button
        className={`flex w-full items-center justify-between bg-white p-2.5 text-xl shadow-sm md:hidden`}
        onClick={toggle}
      >
        {props.storyBlokLabels["label_menu"]}
        {open ? (
          <ChevronUpIcon className="ml-2 h-[16px] text-primary" />
        ) : (
          <ChevronDownIcon className="ml-2 h-[16px] text-primary" />
        )}
      </button>
      <div className={`relative max-h-full w-full md:max-w-80 md:min-w-72`}>
        <div
          className={cn(
            `
              absolute inset-x-0 z-50 max-h-full min-h-[calc(100vh-1rem)] w-full overflow-x-hidden overflow-y-auto
              bg-black/30
              md:relative md:inset-0 md:inset-x-auto md:z-auto md:block md:max-h-none md:min-h-fit md:w-auto
              md:rounded-sm md:border md:border-gray-200 md:bg-white
            `,
            {
              hidden: !open,
            }
          )}
          onClick={toggle}
        >
          <nav className={cn(`m-auto w-[calc(100%-100px)] text-base font-bold md:block md:w-auto`)}>
            {accountNavigationLinks.map((navigationLink) => (
              <div key={navigationLink.id}>
                {!navigationLink.hidden &&
                  (navigationLink.ociOnly ? props.isOci : true) &&
                  (navigationLink.b2bOnly ? props.isB2B : true) && (
                    <AccountNavigationLink
                      sbLocale={props.sbLocale}
                      {...navigationLink}
                      active={navigationLink.id === props.activeMenuItem}
                      text={props.storyBlokLabels[`label_${navigationLink.id}`]}
                    />
                  )}
              </div>
            ))}
            <div
              className={cn(
                `
                  flex w-full items-center bg-card pr-2.5 pb-2.5 pl-3 leading-7 font-normal
                  hover:font-bold hover:text-primary
                `,
                {
                  hidden: hasOCIUserRole,
                }
              )}
            >
              <StoryblokLogoutButton sbLocale={props.sbLocale} />
            </div>
          </nav>
        </div>
      </div>
    </>
  )
}
